import type { BlockType } from '@unifyapps/defs/types/block';
import type { DataSource } from '@unifyapps/defs/types/dataSource';
import type { DependencyGraphManager } from '../../../../../../dependency/manager/DependencyGraphManager';
import DataSourceHelper from '../../../../../helper/DataSourceHelper';
import { DEFAULT_INFINITE_PAGINATION_INFO } from '../const';
import { getInfinitePaginationDetailsForBlocks } from './getInfinitePaginationDetailsForBlocks';

/**
 * Determines if a datasource requires infinite pagination and retrieves its configuration
 *
 * This function examines a datasource and all blocks that use it to check if any of them
 * require infinite pagination. If so, it returns the pagination configuration for the first
 * infinitely paginated block found.
 *
 * @param blocks - Array of block definitions to check
 * @param dataSource - The datasource to check for infinite pagination
 * @param dataSourceId - The ID of the datasource to check
 * @returns Object containing whether infinite pagination is needed and its configuration
 */
export function getDataSourcePaginationInfo({
  blocks,
  dataSource,
  dataSourceId,
  graphManager,
}: {
  blocks: Record<string, BlockType | undefined> | undefined;
  dataSource: Pick<DataSource, 'context' | 'inputs'>;
  dataSourceId: string;
  graphManager: DependencyGraphManager;
}) {
  const impactedEntities = graphManager.getNonTransitiveImpactedNode(dataSourceId);

  // First check if the datasource itself supports infinite pagination
  if (DataSourceHelper.isInfiniteDataSourceWorkflowNode(dataSource)) {
    // For each block that uses this datasource
    for (const impactedEntityId of Array.from(impactedEntities)) {
      // Check if this block is connected to the datasource we're examining
      if (blocks?.[impactedEntityId]) {
        // Get infinite pagination details for this specific block and datasource
        const infinitePaginationDetails = getInfinitePaginationDetailsForBlocks({
          block: blocks[impactedEntityId],
          dataSource,
          dataSourceId,
        });

        // Skip blocks that don't use infinite pagination
        if (!infinitePaginationDetails.isInfinite) {
          continue;
        }

        // Return the first infinitely paginated configuration found
        return {
          isInfinite: true,
          infinitePaginationInfo: infinitePaginationDetails.infinitePaginationInfo,
        };
      }
    }
  }

  // Return default (non-infinite) pagination if no matching configuration was found
  return { isInfinite: false, infinitePaginationInfo: DEFAULT_INFINITE_PAGINATION_INFO };
}
